import React from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import { BOOKING_DEADLINE_HOURS } from '@common/constants/academyReworkings';
import { canBook, overlaps, parseDate, timeLabel } from '@common/helpers/academyReworkings';
import { TimeWindow } from '@common/types/academyReworkings';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { nextButtonSx } from '../../styled';
import ErrorNotice from '../ErrorNotice';

function SlotButton({ window, flow }: { window: TimeWindow; flow: BookingFlow }) {
	const { t } = useTranslation();
	const selected = flow.times.some((time) => time.window.key === window.key);
	const disabled =
		!selected &&
		(!canBook(window) ||
			flow.times.length >= flow.pendingLessons.length ||
			flow.times.some((time) => overlaps(time.window, window)));
	return (
		<Tooltip title={t('academyReworkings.tutorsAvailable', { count: window.slots.length })}>
			<span>
				<Button
					fullWidth
					variant={selected ? 'contained' : 'outlined'}
					aria-pressed={selected}
					aria-label={t('academyReworkings.selectTime', {
						date: parseDate(window.start).toFormat('dd.MM'),
						time: timeLabel(window),
					})}
					disabled={disabled}
					onClick={() => flow.toggleTime(window)}
					sx={{
						position: 'relative',
						p: '6px 8px',
						minHeight: '56px',
						borderRadius: '12px',
						display: 'flex',
						flexDirection: 'column',
						gap: '2px',
						textTransform: 'none',
						...(!selected && { bgcolor: 'action.hover' }),
					}}
				>
					<Box component="span" display="flex" alignItems="center" gap="6px">
						<Typography
							component="span"
							sx={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap' }}
						>
							{parseDate(window.start).toFormat('HH:mm')}
						</Typography>
						{selected && <CheckCircleOutlineIcon sx={{ fontSize: '16px', flexShrink: 0 }} />}
					</Box>
					<Typography component="span" variant="caption" sx={{ whiteSpace: 'nowrap' }}>
						{selected ? t('academyReworkings.selected') : timeLabel(window)}
					</Typography>
				</Button>
			</span>
		</Tooltip>
	);
}

export default function Times({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	const selectedTimes = [...flow.times].sort(
		(a, b) => parseDate(a.window.start).toMillis() - parseDate(b.window.start).toMillis(),
	);
	const complete =
		flow.times.length === flow.pendingLessons.length &&
		flow.times.every((item) => canBook(item.window));
	return (
		<Box display="flex" flexDirection="column" gap="16px">
			<Box>
				<Typography variant="h3">{t('academyReworkings.chooseTimes')}</Typography>
				<Typography color="text.secondary" sx={{ mt: 1 }}>
					{t('academyReworkings.timesHint', { count: flow.pendingLessons.length })}
				</Typography>
			</Box>
			{flow.slotsError && <ErrorNotice message={flow.slotsError} onRetry={flow.retrySlots} />}
			{flow.slotsLoading ? (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			) : (
				flow.selectedDays.map((day) => {
					const windows = flow.availableWindows.filter(
						(window) => parseDate(window.start).toISODate() === day,
					);
					return (
						<Paper key={day} sx={{ p: 2, minWidth: 0, borderRadius: '16px' }}>
							<Box
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								gap="12px"
								flexWrap="wrap"
								mb={1.5}
							>
								<Typography variant="h4" sx={{ textTransform: 'capitalize' }}>
									{DateTime.fromISO(day).toFormat('cccc, dd.MM')}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{t(
										windows.length
											? 'academyReworkings.availableTimes'
											: 'academyReworkings.noTimes',
										{ count: windows.length },
									)}
								</Typography>
							</Box>
							{windows.length ? (
								<Box
									sx={(theme) => ({
										display: 'grid',
										minWidth: 0,
										gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
										[theme.breakpoints.up('sm')]: {
											gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
										},
										gap: '8px',
									})}
								>
									{windows.map((window) => (
										<SlotButton key={window.key} window={window} flow={flow} />
									))}
								</Box>
							) : (
								<Typography color="text.secondary">{t('academyReworkings.dayNoSlots')}</Typography>
							)}
						</Paper>
					);
				})
			)}
			<Typography variant="body2" color="text.secondary">
				{t('academyReworkings.timeZone')}{' '}
				{t('academyReworkings.bookingDeadline', { hours: BOOKING_DEADLINE_HOURS })}
			</Typography>
			<Paper
				sx={{
					position: 'sticky',
					bottom: '16px',
					zIndex: 1,
					p: 2,
					borderRadius: '16px',
					border: '1px solid',
					borderColor: 'divider',
					backdropFilter: 'blur(16px)',
				}}
			>
				{selectedTimes.length > 0 && (
					<Box display="flex" gap="8px" flexWrap="wrap" mb={2}>
						{selectedTimes.map((time) => (
							<Chip
								key={time.window.key}
								label={`${parseDate(time.window.start).toFormat('dd.MM')} · ${timeLabel(time.window)}`}
								onDelete={() => flow.toggleTime(time.window)}
							/>
						))}
					</Box>
				)}
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					flexWrap="wrap"
					gap="16px"
				>
					<Typography fontWeight={600}>
						{t('academyReworkings.selectedTimes', {
							count: flow.times.length,
							total: flow.pendingLessons.length,
						})}
					</Typography>
					<Button
						variant="contained"
						sx={nextButtonSx}
						disabled={!complete || flow.slotsLoading || !!flow.slotsError}
						onClick={() => flow.setStep('tutors')}
					>
						{t('academyReworkings.nextTutor')}
					</Button>
				</Box>
			</Paper>
		</Box>
	);
}
